import { es } from '../i18n/es.js';
import { isPermittedPlaceholder, placeholdersOf } from '../i18n/keys.js';
/**
 * One validation issue, as the `details` entry a client can actually render.
 *
 * **One mapper, because there used to be two and they disagreed.** `http/validate.ts` mapped every
 * issue the request edge raised and `fields/validate.ts` mapped every issue a compiled person-field
 * validator raised; the second filled the numbers its messages asked for and the first did not, so
 * the SAME failure reached a client as "enter at least 3 characters" from one layer and as "the
 * format is not valid" from the other. This is that logic, once, taking the two things that honestly
 * differ between callers — the path namespace, and how an unrecognised key expands — as arguments.
 *
 * **The rule the whole file exists to enforce: a key may be chosen only if every placeholder it asks
 * for can be filled from THIS issue.** `interpolate` leaves an unfilled placeholder standing, on
 * purpose, so that a missing value is visible rather than disguised as finished prose — which means
 * a mapper that picks a message it cannot complete is a mapper that prints `{{min}}` to whoever is
 * reading the screen. That is not hypothetical: `errors.validation.out_of_range` read "enter a value
 * between {{min}} and {{max}}", the library raises `too_small` and `too_big` as SEPARATE issues so
 * no single issue ever carried both, and sixteen schemas declared it.
 *
 * **The issue's `origin` decides what a number means; the message only gets to ask for a name.**
 * `issue.maximum` counts characters for a string, elements for a list, a ceiling for a number and
 * bytes for a file. Letting the message choose — "this key wants `{{limit}}`, so put `maximum` in
 * it" — is how a bound on a string ends up rendered as a bound on a count, correct by luck, with
 * nothing able to notice. So the tables below are keyed by origin and a message may draw only from
 * what its own origin admits.
 *
 * **A refinement carries no bound at all**, which is the other half of the same problem: every
 * `.refine()` and `ctx.addIssue` produces a `custom` issue with no `minimum` or `maximum`. What it
 * CAN carry is its own `params`, which the library copies onto the issue verbatim — so a refinement
 * that needs a number declares it beside its key. That channel is also the one place a schema author
 * could put a submitted value into a body that is logged and mailed, so {@link declaredParams} is a
 * filter rather than a copy: an allowlisted name, and a number or a machine-shaped string, or it is
 * dropped.
 *
 * **Nothing here reads `issue.input`.** The input is the passport number, the telephone, the name.
 * The only thing ever echoed is a property NAME — from an unrecognised-keys issue, or from the path
 * the library built — and only through the two grammars in {@link EchoStyle}, because a client may
 * submit any property name at all and it travels from here into a response body and a log line.
 *
 * **What this file guarantees is COMPLETENESS, not appropriateness.** A key that interpolates
 * nothing is always fillable and therefore always choosable, so a schema declaring
 * `errors.system.maintenance` on a text field would be forwarded verbatim and render "the system is
 * under maintenance" against a control. Nothing here can tell a field sentence from a system one;
 * what refuses that is `tools/i18n/schema-keys.ts`, which checks the declared key's own HTTP status.
 */
/** The grammar a submitted name must satisfy before it is echoed back. Same shape as a machine key. */
const ECHOABLE_KEY = /^[a-z][a-z0-9_]{0,63}$/u;
/**
 * The grammar a PATH segment must satisfy, which is wider and for a different reason.
 *
 * A path is built from what the schema declares — `idempotencyKey`, `createdTo`, `Idempotency-Key`,
 * an array index — so it has to admit capitals, digits and hyphens, where a message interpolating a
 * name admits only a machine key. What it must still exclude is anything a CLIENT chose: a record's
 * keys are the caller's own strings and the library puts them straight into `issue.path`, so
 * `z.record(machineKeySchema, …)` on a person's fields would otherwise carry
 * `body.fields.<script>alert(1)</script>` into a response body and a log line.
 */
const ECHOABLE_SEGMENT = /^[A-Za-z0-9_-]{1,64}$/u;
/**
 * Whether a string may be interpolated into a message as a schema identifier.
 *
 * The tight grammar, because this is a VALUE going into a sentence that is rendered on a shared
 * screen and can be mailed: `{{fieldKey}}` and `{{optionValue}}`-class names are machine keys and
 * nothing else should pass for one.
 */
export function isEchoableKey(value) {
    return ECHOABLE_KEY.test(value);
}
function isEchoableName(value, style) {
    return style === 'machine-key' ? ECHOABLE_KEY.test(value) : ECHOABLE_SEGMENT.test(value);
}
/**
 * The dotted path of one issue, truncated at the first segment that cannot be repeated.
 *
 * Truncating rather than substituting, because a container path is a true statement — something in
 * here was refused — where a placeholder segment would be a path no client could match against
 * anything. `fields/validate.ts` has always fallen back to the container for the same reason.
 */
function pathOf(prefix, issue) {
    // **An `invalid_key` ends at the client's own string**, and it is the key itself that failed, so
    // the last segment is dropped whatever it looks like. `body.fields` says "one of the names you
    // sent is not a field key", which is the whole of what can be said without repeating it.
    const segments = issue.code === 'invalid_key' ? issue.path.slice(0, -1) : issue.path;
    const parts = [prefix];
    for (const segment of segments) {
        const text = String(segment);
        if (!ECHOABLE_SEGMENT.test(text)) {
            break;
        }
        parts.push(text);
    }
    return parts.join('.');
}
/**
 * The origins whose bound counts whole things.
 *
 * It decides one narrow question: whether an EXCLUSIVE bound can be restated as an inclusive one.
 * "More than 2 characters" is "at least 3"; "greater than 2.5" is not "at least 3.5", so a
 * non-integral origin with an exclusive bound sources nothing and the message falls back.
 *
 * **`number` is not on this list even when the schema was `z.int()`**, and that is a limit of what
 * the issue says rather than an oversight: the library reports `origin: 'number'` for an integer
 * schema, so `z.int().positive()` and `z.number().gt(0)` arrive indistinguishable. Printing "the
 * smallest value allowed is 1" for the second would be a message that states the opposite of the
 * rule, so both fall back.
 *
 * **The consequence for a schema author: on a request path, write `.min(n)` and never `.positive()`
 * or `.gt(n)` on a whole number.** The two rules are identical for an integer and only one of them
 * can be rendered. `routes/credential-batches`'s serial parameter was the one place in the product
 * that had it the other way round, and it answered "this is not valid" where "the smallest value
 * allowed is 1" was exactly true.
 */
const INTEGRAL_ORIGINS = new Set(['string', 'bigint', 'array', 'set']);
/**
 * The placeholder names a bound supplies, grouped by the SIDE that supplies them.
 *
 * Exported because the build-time check in `tools/i18n/schema-keys.ts` has to ask the same question
 * this file answers — can ONE issue carry these names together? — and a second hand-kept copy of the
 * grouping is a check on a fiction the day either side gains a name.
 */
export const BOUND_PLACEHOLDER_SIDES = [
    ['min', 'minLength'],
    ['max', 'maxLength', 'limit'],
];
const TOO_SMALL = new Map([
    ['string', { names: ['minLength'], key: 'errors.validation.too_short' }],
    ['number', { names: ['min'], key: 'errors.validation.below_minimum' }],
    ['int', { names: ['min'], key: 'errors.validation.below_minimum' }],
    ['bigint', { names: ['min'], key: 'errors.validation.below_minimum' }],
    ['array', { names: ['min'], key: 'errors.validation.too_few_items' }],
    ['set', { names: ['min'], key: 'errors.validation.too_few_items' }],
]);
const TOO_BIG = new Map([
    ['string', { names: ['maxLength'], key: 'errors.validation.too_long' }],
    ['number', { names: ['max'], key: 'errors.validation.above_maximum' }],
    ['int', { names: ['max'], key: 'errors.validation.above_maximum' }],
    ['bigint', { names: ['max'], key: 'errors.validation.above_maximum' }],
    ['array', { names: ['max', 'limit'], key: 'errors.validation.too_many_items' }],
    ['set', { names: ['max', 'limit'], key: 'errors.validation.too_many_items' }],
]);
/**
 * The line a detail falls back to. It interpolates nothing, so it is always renderable.
 *
 * Not `errors.validation.failed`, which is the ENVELOPE's own message: a detail renders beneath it
 * as "Nacionalidad: <this>", so borrowing the envelope put a plural sentence against one control and
 * repeated the banner a line above it word for word.
 */
const GENERIC_KEY = 'errors.validation.unspecified';
function isBound(issue) {
    return issue.code === 'too_small' || issue.code === 'too_big';
}
/**
 * What one bound issue can honestly report, or `null`.
 *
 * `exact` is the interesting refusal: `z.string().length(5)` raises BOTH a `too_small` and a
 * `too_big` carrying `exact: true`, and "enter at least 5 characters" is the wrong sentence for a
 * field that takes exactly five. Neither bound message may be chosen for one.
 */
function boundFacts(issue) {
    if (issue.exact === true) {
        return null;
    }
    const origin = String(issue.origin);
    const facts = (issue.code === 'too_small' ? TOO_SMALL : TOO_BIG).get(origin);
    if (facts === undefined) {
        return null;
    }
    const raw = issue.code === 'too_small' ? issue.minimum : issue.maximum;
    const bound = Number(raw);
    if (!Number.isFinite(bound)) {
        return null;
    }
    if (issue.inclusive === false) {
        if (!INTEGRAL_ORIGINS.has(origin)) {
            return null;
        }
        return { facts, value: issue.code === 'too_small' ? bound + 1 : bound - 1 };
    }
    return { facts, value: bound };
}
/** The unrecognised property names this issue may repeat, in the order they arrived. */
function echoableKeysOf(issue, style) {
    return issue.code === 'unrecognized_keys'
        ? issue.keys.filter((key) => isEchoableName(key, style))
        : [];
}
/**
 * Whether a value a schema declared beside its key may be interpolated at all.
 *
 * A finite number, or a string shaped like a machine key. Nothing else: a schema author cannot be
 * stopped from writing `params: { count: submittedValue }`, and this is the line between a bound
 * and a person's own words reaching a log and an e-mail.
 */
function isInterpolable(value) {
    return typeof value === 'number'
        ? Number.isFinite(value)
        : typeof value === 'string' && isEchoableKey(value);
}
/**
 * The values a `custom` issue declared beside its key, filtered.
 *
 * `$ZodIssueCustom.params` is `Record<string, any>`, so this is a security boundary and not tidying:
 * it is the one channel through which a schema could put a submitted value into an error body, a log
 * line and an e-mail. An entry survives only if its NAME is on the interpolation allowlist and its
 * VALUE is a finite number or a machine-shaped string. Anything else is dropped, which leaves the
 * placeholder standing — the designed, visible failure.
 */
function declaredParams(issue) {
    const params = {};
    if (issue.code !== 'custom' || issue.params === undefined) {
        return params;
    }
    for (const [name, value] of Object.entries(issue.params)) {
        if (isPermittedPlaceholder(name) && isInterpolable(value)) {
            params[name] = value;
        }
    }
    return params;
}
/** Everything one issue can supply, by placeholder name. The only source a detail ever draws on. */
function sourcesOf(issue, style) {
    const sources = declaredParams(issue);
    if (isBound(issue)) {
        const bound = boundFacts(issue);
        if (bound !== null) {
            for (const name of bound.facts.names) {
                sources[name] = bound.value;
            }
        }
    }
    const echoable = echoableKeysOf(issue, style);
    if (echoable.length > 0) {
        sources.fieldPath = echoable.join(', ');
    }
    return sources;
}
/**
 * The values one key needs, drawn from one issue, or `null` when the issue cannot complete it.
 *
 * A key that interpolates nothing yields an empty map rather than `null`: it is always renderable,
 * which is what makes {@link GENERIC_KEY} and every static sentence in the catalogue safe to choose.
 */
function fill(key, sources) {
    const params = {};
    for (const name of placeholdersOf(es[key])) {
        if (!isPermittedPlaceholder(name)) {
            return null;
        }
        const value = sources[name];
        if (value === undefined) {
            return null;
        }
        params[name] = value;
    }
    return params;
}
/**
 * The message the issue's own shape resolves to, for the codes that carry no value of their own.
 *
 * `invalid_value` is every bare `z.enum()` and `z.literal()` on a request path — a sort direction, a
 * granularity, a setting key — and without it they all read as the unspecified line where "choose
 * one of the available options" is exactly the sentence.
 */
const SHAPE_KEYS = new Map([
    ['invalid_value', 'errors.validation.invalid_option'],
    ['invalid_format', 'errors.validation.invalid_format'],
]);
/** The message a key resolves to when the schema declared none, or `null` for "nothing to say". */
function structuralKeyOf(issue, style) {
    if (issue.code === 'invalid_type') {
        // An absent value and a wrong one are different mistakes, and only the first is "required".
        return issue.input === undefined
            ? 'errors.validation.required'
            : 'errors.validation.invalid_format';
    }
    const byShape = SHAPE_KEYS.get(issue.code);
    if (byShape !== undefined) {
        return byShape;
    }
    if (issue.code === 'unrecognized_keys') {
        return echoableKeysOf(issue, style).length > 0 ? 'errors.validation.unknown_field' : null;
    }
    if (isBound(issue)) {
        const bound = boundFacts(issue);
        if (bound === null) {
            return null;
        }
        // The library clamps an out-of-range integer against `Number.MAX_SAFE_INTEGER`, and "the
        // largest value allowed is 9007199254740991" is technically true and useless. It is a wrong
        // TYPE of number, which is what the format sentence says.
        if (!Number.isSafeInteger(bound.value) || Math.abs(bound.value) >= Number.MAX_SAFE_INTEGER) {
            return 'errors.validation.invalid_format';
        }
        // "At least one character" is what required MEANS, and there are a hundred undeclared
        // `.min(1)`s in the schemas: without this they would all read "enter at least 1 characters".
        if (issue.code === 'too_small' && String(issue.origin) === 'string' && bound.value <= 1) {
            return 'errors.validation.required';
        }
        return bound.facts.key;
    }
    return null;
}
/** Whether a string names a message the catalogue carries. The parity gate makes one side enough. */
function isMessageKey(value) {
    return Object.hasOwn(es, value);
}
/**
 * The message key one issue resolves to.
 *
 * **The schema's own declaration is consulted first, and that ordering is load-bearing.** Every
 * refinement in this package supplies its catalogue key through the library's `error` option, which
 * replaces the message and leaves the structural code alone — so a `minLength` on a text field
 * produces `too_small` carrying `errors.validation.too_short`, and reading the code first would
 * throw away the specific key the schema chose. `errors.validation.invalid_phone` would reach an
 * operator as "some of the information is not valid" for every check written in the commoner style.
 *
 * It is consulted first and is not obeyed blindly: a declaration this issue cannot complete is
 * demoted rather than printed with its braces showing.
 */
export function messageKeyOfIssue(issue, style = 'property-name') {
    const reported = reportedIssue(issue);
    const sources = sourcesOf(reported, style);
    if (isMessageKey(reported.message) && fill(reported.message, sources) !== null) {
        return reported.message;
    }
    return structuralKeyOf(reported, style) ?? GENERIC_KEY;
}
/**
 * The issue whose REASON is reported, unwrapping a record key's own failure exactly one level.
 *
 * A record's key failed its own schema, and the offender is the key — whose name is a client string
 * this must not echo — so the outer path is what a caller is told about and the inner issue is only
 * consulted for why. One level rather than a walk: a nested wrapper would be a shape no schema here
 * produces, and unbounded recursion over attacker-shaped input is its own problem.
 */
function reportedIssue(issue) {
    if (issue.code !== 'invalid_key' && issue.code !== 'invalid_element') {
        return issue;
    }
    return issue.issues[0] ?? issue;
}
/** The values that key needs, or nothing. Total by construction: a key it cannot fill is never chosen. */
export function paramsOfIssue(key, issue, style = 'property-name') {
    const params = fill(key, sourcesOf(reportedIssue(issue), style)) ?? {};
    return Object.keys(params).length === 0 ? undefined : params;
}
function detailOf(path, issue, style) {
    const messageKey = messageKeyOfIssue(issue, style);
    const params = paramsOfIssue(messageKey, issue, style);
    return params === undefined ? { path, messageKey } : { path, messageKey, params };
}
/**
 * One issue, as the details it reports. `unrecognized_keys` is the only one that fans out.
 *
 * **One detail per key, always.** There used to be a second style that collapsed them onto the
 * first and listed every name in the one message — and the message is singular, so two unknown
 * fields produced "there is no field called «invented1, invented2»", which claims a single field by
 * that name. One detail per name says something true about each, and a form that does not render a
 * given key simply leaves that one to the banner, which is what it already does.
 */
export function detailsOfIssue(issue, options) {
    const path = pathOf(options.prefix, issue);
    if (issue.code !== 'unrecognized_keys') {
        return [detailOf(path, issue, options.echo)];
    }
    const echoable = echoableKeysOf(issue, options.echo);
    const details = echoable.map((key) => detailOf(`${path}.${key}`, { ...issue, keys: [key] }, options.echo));
    // A key too strange to repeat is still a key that was refused, and a client told nothing about it
    // would be left believing the rest of the body is what failed.
    return echoable.length === issue.keys.length
        ? details
        : [...details, { path, messageKey: GENERIC_KEY }];
}
/** Every issue of one failure, as details. The form a caller with a `ZodError` in hand wants. */
export function detailsOfError(error, options) {
    const seen = new Set();
    // Deduped, because truncating a path can genuinely collapse two issues onto one: two record keys
    // too strange to repeat both report the container, and a client shown the same line twice learns
    // nothing from the second.
    return error.issues.flatMap((issue) => detailsOfIssue(issue, options).filter((detail) => {
        const identity = JSON.stringify([detail.path, detail.messageKey, detail.params ?? null]);
        if (seen.has(identity)) {
            return false;
        }
        seen.add(identity);
        return true;
    }));
}
//# sourceMappingURL=details.js.map